import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Check, FileText, Download } from "lucide-react";

// Public, read-only seller page. Deliberately uses plain axios (not the app's `api` instance) so no
// login token is ever attached or required, and nothing is stored in the browser.
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : null);
const size = (n) => (n == null ? "" : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function SellerPortal() {
    const { token } = useParams();
    const [view, setView] = useState(null);
    const [state, setState] = useState("loading"); // loading | ok | invalid

    useEffect(() => {
        axios.get(`${API}/portal/${encodeURIComponent(token)}`)
            .then((r) => { setView(r.data); setState("ok"); })
            .catch(() => setState("invalid"));
    }, [token]);

    if (state === "loading") return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
    if (state === "invalid") return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-6" data-testid="portal-invalid">
            <div className="text-center max-w-md">
                <h1 className="font-display text-xl font-bold text-[#0B1F3A]">This link isn't available</h1>
                <p className="text-sm text-muted-foreground mt-2">It may have expired or been replaced. Please contact your adviser for a new link.</p>
            </div>
        </div>
    );

    const v = view;
    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="seller-portal-page">
            <header className="bg-[#0B1F3A] text-white px-6 lg:px-10 py-4 flex items-center justify-between">
                <div className="font-display font-bold text-lg">{v.firm_name}</div>
                <div className="text-xs uppercase tracking-widest text-[#C9A227]">Client portal</div>
            </header>
            <main className="px-6 lg:px-10 py-8 grid gap-6 lg:grid-cols-3">
                <section className="lg:col-span-3">
                    <h1 className="font-display text-2xl font-bold text-[#0B1F3A]" data-testid="portal-company">{v.company_name}</h1>
                    {v.welcome_message && <p className="mt-2 text-sm text-[#334155] whitespace-pre-line max-w-3xl">{v.welcome_message}</p>}
                </section>

                <section className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-md p-6" data-testid="portal-progress">
                    <div className="flex items-baseline justify-between mb-5">
                        <h2 className="font-display font-bold text-[#0B1F3A]">Progress</h2>
                        {v.expected_close && !v.closed && <span className="text-xs text-muted-foreground">Expected completion {fmt(v.expected_close)}</span>}
                    </div>
                    <ol className="flex flex-wrap gap-y-4">
                        {v.timeline.map((s) => (
                            <li key={s.name} className="flex items-center" data-testid={`portal-stage-${s.state}`}>
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] border ${s.state === "done" ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : s.state === "current" ? "bg-[#C9A227] text-white border-[#C9A227]" : "bg-white text-[#94A3B8] border-[#CBD5E1]"}`}>
                                    {s.state === "done" ? <Check className="w-3.5 h-3.5" /> : ""}
                                </span>
                                <span className={`ml-2 mr-5 text-sm ${s.state === "current" ? "font-semibold text-[#0B1F3A]" : s.state === "done" ? "text-[#334155]" : "text-[#94A3B8]"}`}>{s.name}</span>
                            </li>
                        ))}
                    </ol>
                    {v.conditions && (
                        <div className="mt-5 pt-4 border-t border-[#E2E8F0]" data-testid="portal-conditions">
                            <div className="flex justify-between text-sm"><span className="text-[#334155]">Conditions to completion</span><span className="font-mono">{v.conditions.resolved} of {v.conditions.total} resolved</span></div>
                            <div className="h-1.5 rounded bg-neutral-100 mt-2 overflow-hidden"><div className="h-full bg-[#C9A227]" style={{ width: `${v.conditions.completion_pct}%` }} /></div>
                        </div>
                    )}
                </section>

                <section className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-md p-6" data-testid="portal-updates">
                    <h2 className="font-display font-bold text-[#0B1F3A] mb-4">Updates</h2>
                    {v.updates.length === 0 ? <p className="text-sm text-muted-foreground">No updates yet — your adviser will post here as things progress.</p> : (
                        <div className="space-y-5">
                            {v.updates.map((u) => (
                                <article key={u.id}>
                                    <div className="text-xs text-muted-foreground">{fmt(u.posted_at)}</div>
                                    <h3 className="font-medium text-[#0B1F3A]">{u.title}</h3>
                                    <p className="text-sm text-[#334155] whitespace-pre-line mt-0.5">{u.body}</p>
                                </article>
                            ))}
                        </div>
                    )}
                </section>

                <section className="bg-white border border-[#E2E8F0] rounded-md p-6" data-testid="portal-documents">
                    <h2 className="font-display font-bold text-[#0B1F3A] mb-4">Documents</h2>
                    {v.documents.length === 0 ? <p className="text-sm text-muted-foreground">Nothing has been shared yet.</p> : (
                        <ul className="space-y-2">
                            {v.documents.map((d) => (
                                <li key={d.id}>
                                    <a href={`${API}/portal/${encodeURIComponent(token)}/documents/${d.id}`} className="flex items-center gap-2 text-sm text-[#0B1F3A] hover:underline">
                                        <FileText className="w-4 h-4 text-[#C9A227] shrink-0" /><span className="truncate">{d.filename}</span><span className="text-xs text-muted-foreground ml-auto shrink-0">{size(d.size)}</span><Download className="w-3.5 h-3.5 shrink-0" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </main>
            <footer className="px-6 lg:px-10 pb-8 text-xs text-muted-foreground">Confidential. This page is provided by {v.firm_name} for the named client only; please do not forward the link.</footer>
        </div>
    );
}
